import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Dropdown from "../../common/dropDown";
import PageBreadcrumb from "../../common/PageBreadcrumb/PageBreadcrumb";
import { useUserData } from "../../../hooks/useUserData";
import {
  ALL_STUDIES_OPTION,
  DEFAULT_CATEGORY,
  fetchEisfStudies,
  fetchStudyOperationalData,
  getCachedEisfStudies,
} from "./isfDashboardService";
import {
  aggregateCells,
  buildCommandCenterModel,
  CC_COLORS,
  filedAwaitingSign,
  formatGbDate,
  readinessCssVar,
  readinessHex,
  scopeCells,
  signatureAggregate,
} from "./commandCenterModel";
import { fetchRoleLookup } from "../../../services/lookupService";
import IsfPhaseFilter from "./IsfPhaseFilter";
import StudyGateProgressStrip from "./StudyGateProgressStrip";
import useStudyGateView from "./useStudyGateView";
import "./IsfCommandCenter.scss";

const CATEGORY_OPTIONS = [
  { label: "All (eISF + ePSF)", value: "" },
  { label: "eISF", value: "eISF" },
  { label: "ePSF", value: "ePSF" },
];

const ALL_SITES = { label: "All sites", value: "all" };

const TABS = [
  { id: "C", label: "Command center", hero: true },
  { id: "S", label: "Signatures" },
  { id: "1", label: "Inspection readiness" },
  { id: "2", label: "Missing required" },
  { id: "3", label: "Overdue & at-risk" },
  { id: "4", label: "Site comparison" },
];

function formatGaugePercent(percent) {
  const raw = Math.min(100, Math.max(0, Number(percent) || 0));
  // Whole numbers stay compact ("100"); otherwise one decimal ("25.2").
  if (Math.abs(raw - Math.round(raw)) < 0.05) {
    return { numeric: Math.round(raw), text: String(Math.round(raw)) };
  }
  const one = Math.round(raw * 10) / 10;
  return { numeric: one, text: one.toFixed(1) };
}

function Gauge({ percent, label, size = 150 }) {
  const { numeric: p, text: valueText } = formatGaugePercent(percent);
  const R = size * 0.38;
  const strokeW = size * 0.072;
  const cx = size / 2;
  const cy = size / 2;
  const circ = 2 * Math.PI * R;
  const off = circ * (1 - p / 100);
  const stroke = readinessHex(p);

  // Fit number + % inside the ring for 1–3 digits (and optional decimal).
  const innerWidth = Math.max(24, 2 * R - strokeW * 2 - size * 0.1);
  const charUnits = valueText.length + 0.55; // "%" is narrower
  const valueFont = Math.min(size * 0.2, (innerWidth / charUnits) * 1.05);
  const pctFont = Math.max(10, valueFont * 0.52);
  const labelFont = Math.min(size * 0.085, 14);
  const valueY = label ? cy - size * 0.04 : cy;
  const labelY = cy + valueFont * 0.55 + size * 0.04;

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      aria-hidden
      className="isf-cc__gauge-svg"
    >
      <circle
        cx={cx}
        cy={cy}
        r={R}
        fill="none"
        stroke="#eef2f1"
        strokeWidth={strokeW}
      />
      <circle
        cx={cx}
        cy={cy}
        r={R}
        fill="none"
        stroke={stroke}
        strokeWidth={strokeW}
        strokeLinecap="round"
        strokeDasharray={circ}
        strokeDashoffset={off}
        transform={`rotate(-90 ${cx} ${cy})`}
      />
      <text
        x={cx}
        y={valueY}
        textAnchor="middle"
        dominantBaseline="middle"
        fill={stroke}
      >
        <tspan fontSize={valueFont} fontWeight="800">
          {valueText}
        </tspan>
        <tspan fontSize={pctFont} fontWeight="700" dx={size * 0.012}>
          %
        </tspan>
      </text>
      {label ? (
        <text
          x={cx}
          y={labelY}
          textAnchor="middle"
          dominantBaseline="hanging"
          fontSize={labelFont}
          fontWeight="500"
          fill="#6a7a77"
        >
          {label}
        </text>
      ) : null}
    </svg>
  );
}

function StatusBadge({ status }) {
  if (status === "fulfilled") return <span className="isf-cc__badge isf-cc__badge--ful">Fulfilled</span>;
  if (status === "pending") return <span className="isf-cc__badge isf-cc__badge--pen">Pending</span>;
  if (status === "overdue") return <span className="isf-cc__badge isf-cc__badge--ovd">Overdue</span>;
  return <span className="isf-cc__badge isf-cc__badge--na">N/A</span>;
}

function SigBadge({ cell }) {
  if (!cell.reqSig) return <span className="isf-cc__badge isf-cc__badge--na">n/a</span>;
  if (cell.sig === "signed") return <span className="isf-cc__badge isf-cc__badge--sg">Signed</span>;
  return <span className="isf-cc__badge isf-cc__badge--aw">Awaiting</span>;
}

function KpiCard({ title, value, detail, color, barColor, filterKey, selected = false, onClick }) {
  return (
    <div
      className={`isf-cc__kpi${selected ? " sel" : ""}`}
      role="button"
      tabIndex={0}
      onClick={() => onClick(filterKey)}
      onKeyDown={(e) => e.key === "Enter" && onClick(filterKey)}
    >
      <span className="isf-cc__kbar" style={{ background: barColor || "var(--cc-line)" }} />
      <div className="isf-cc__kt">{title}</div>
      <div className="isf-cc__kv" style={{ color: color || "var(--cc-ink)" }}>
        {value}
      </div>
      {detail ? <div className="isf-cc__kd" dangerouslySetInnerHTML={{ __html: detail }} /> : null}
    </div>
  );
}

export default function IsfCommandCenter() {
  const navigate = useNavigate();
  const { userData } = useUserData();
  const loadRef = useRef(0);

  const [studies, setStudies] = useState([]);
  const [studiesLoading, setStudiesLoading] = useState(true);
  const [study, setStudy] = useState(null);
  const [category, setCategory] = useState(DEFAULT_CATEGORY);
  const [site, setSite] = useState(ALL_SITES);
  const [tab, setTab] = useState("C");
  const [mmode, setMmode] = useState("docs");
  const [sel, setSel] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [roleOptions, setRoleOptions] = useState([]);
  const [payload, setPayload] = useState(null);
  const [asOf, setAsOf] = useState(() => new Date());

  const {
    studyGate,
    studyGateList,
    studyGateProgress,
    selectedGateCodes,
    setSelectedGateCodes,
    effectiveGateCodes,
    phaseLabel,
    gateLoading,
  } = useStudyGateView({
    userData,
    studyId: study?.value,
  });

  const studyOptions = useMemo(
    () => studies.map((s) => ({ label: s.StudyName, value: s.StudyId })),
    [studies],
  );

  const model = useMemo(() => {
    if (!payload) return null;
    return buildCommandCenterModel({
      summary: payload.readiness?.summary,
      placeholderDetails: payload.readiness?.placeholderDetails,
      milestoneSummary: payload.readiness?.milestoneSummary,
      atRiskDetail: payload.readiness?.atRiskDetail,
      siteComparison: payload.readiness?.siteComparison,
      placeholders: payload.placeholders,
      documents: payload.documents || [],
      roleOptions,
    });
  }, [payload, roleOptions]);

  const siteOptions = useMemo(() => {
    const sites = model?.sites?.filter((s) => s.id !== "__study__") ?? [];
    return [ALL_SITES, ...sites.map((s) => ({ label: s.name, value: s.id }))];
  }, [model]);

  const scopedCells = useMemo(
    () => (model ? scopeCells(model.cells, site?.value) : []),
    [model, site?.value],
  );

  const docAgg = useMemo(() => aggregateCells(scopedCells), [scopedCells]);
  const sigAgg = useMemo(() => signatureAggregate(scopedCells), [scopedCells]);
  const filedUnsigned = useMemo(() => filedAwaitingSign(scopedCells), [scopedCells]);

  const loadStudies = useCallback(async () => {
    if (!userData?.OrganizationId) {
      setStudiesLoading(false);
      return;
    }
    const cached = getCachedEisfStudies(userData.OrganizationId, userData.StaffId);
    if (cached?.studies?.length) {
      setStudies(cached.studies);
      setStudy((prev) => {
        if (prev?.value) return prev;
        return { label: cached.studies[0].StudyName, value: cached.studies[0].StudyId };
      });
      setStudiesLoading(false);
      return;
    }
    setStudiesLoading(true);
    try {
      const { studies: list } = await fetchEisfStudies({
        organizationId: userData.OrganizationId,
        createdBy: userData.StaffId,
      });
      setStudies(list);
      if (list.length) {
        setStudy({ label: list[0].StudyName, value: list[0].StudyId });
      }
    } catch (err) {
      console.error(err);
      setStudies([]);
    } finally {
      setStudiesLoading(false);
    }
  }, [userData]);

  const loadCenter = useCallback(async () => {
    if (!userData?.OrganizationId || !study?.value) return;
    const runId = ++loadRef.current;
    setLoading(true);
    setLoadError("");
    try {
      const [data, roles] = await Promise.all([
        fetchStudyOperationalData({
          organizationId: userData.OrganizationId,
          createdBy: userData.StaffId,
          studyId: study.value,
          templateCategory: category?.value || null,
          gateCodes: effectiveGateCodes,
        }),
        fetchRoleLookup(userData).catch(() => []),
      ]);
      if (runId !== loadRef.current) return;
      setPayload(data);
      setRoleOptions(roles);
      setAsOf(new Date());
    } catch (err) {
      if (runId !== loadRef.current) return;
      console.error(err);
      setLoadError("Dashboard data could not be loaded.");
      setPayload(null);
    } finally {
      if (runId === loadRef.current) setLoading(false);
    }
  }, [userData, study?.value, category?.value, effectiveGateCodes]);

  useEffect(() => {
    loadStudies();
  }, [loadStudies]);

  useEffect(() => {
    loadCenter();
  }, [loadCenter]);

  const refreshDashboard = useCallback(() => {
    loadCenter();
  }, [loadCenter]);

  const toggleSel = (key) => {
    setSel((prev) => (prev?.k === key ? null : { k: key }));
  };

  const filterCells = useCallback(() => {
    let cs = scopedCells;
    if (!sel) return cs;
    if (sel.ms) cs = cs.filter((c) => c.code === sel.ms);
    if (sel.site) cs = cs.filter((c) => c.siteId === sel.site);
    if (sel.k === "pending") cs = cs.filter((c) => c.status === "pending");
    else if (sel.k === "overdue") cs = cs.filter((c) => c.status === "overdue");
    else if (sel.k === "await") cs = cs.filter((c) => c.reqSig && c.sig === "await");
    else if (sel.k === "atrisk") {
      cs = cs.filter(
        (c) => c.status === "overdue" || (c.status === "fulfilled" && c.sig === "await"),
      );
    }     else if (sel.k) cs = cs.filter((c) => c.bucket === sel.k);
    if (sel.role) cs = cs.filter((c) => c.signerRole === sel.role);
    return cs;
  }, [scopedCells, sel]);

  const openIsf = (cell) => {
    const cat = category?.value || "eISF";
    const gateForLink =
      effectiveGateCodes[0] || studyGateProgress.actualStudyGateCode || "";
    const gateQuery = gateForLink
      ? `&viewGateCode=${encodeURIComponent(gateForLink)}`
      : "";
    navigate(
      `/isf?studyId=${encodeURIComponent(study.value)}&category=${encodeURIComponent(cat)}${gateQuery}`,
    );
  };

  const renderMatrix = (mode, onCellClick) => {
    if (!model?.milestones?.length || !model?.sites?.length) {
      return <p className="isf-cc__note">No milestone × site data for this study.</p>;
    }
    const matrixCells = scopedCells;
    const sites =
      site?.value && site.value !== "all"
        ? model.sites.filter((s) => s.id === site.value)
        : model.sites.filter((s) => s.id !== "__study__");
    const head = (
      <tr>
        <th />
        {model.milestones.map((m) => (
          <th key={m.code}>
            <div className="rot">{m.code}</div>
          </th>
        ))}
        <th className="mrow">{mode === "sig" ? "Signed" : "Ready"}</th>
      </tr>
    );
    const body = sites.map((s) => {
      const rowCells = matrixCells.filter((c) => c.siteId === s.id);
      const tds = model.milestones.map((m) => {
        const c = rowCells.find((x) => x.code === m.code);
        if (!c || c.status === "na") {
          return (
            <td key={m.code}>
              <div className="isf-cc__mc faint">·</div>
            </td>
          );
        }
        if (mode === "sig") {
          if (!c.reqSig) {
            return (
              <td key={m.code}>
                <div className="isf-cc__mc faint" title="No signature required">
                  –
                </div>
              </td>
            );
          }
          const sym = c.sig === "signed" ? "✓" : "✎";
          const sigMod = c.sig === "signed" ? "signed" : "await";
          return (
            <td key={m.code}>
              <div
                className={`isf-cc__mc isf-cc__mc--${sigMod}`}
                role="button"
                tabIndex={0}
                onClick={() => onCellClick?.(c)}
              >
                {sym}
              </div>
            </td>
          );
        }
        const sym = { fulfilled: "✓", pending: "•", overdue: "✕" }[c.status] || "•";
        const statusMod =
          c.status === "fulfilled" || c.status === "pending" || c.status === "overdue"
            ? c.status
            : "pending";
        return (
          <td key={m.code}>
            <div
              className={`isf-cc__mc isf-cc__mc--${statusMod}`}
              role="button"
              tabIndex={0}
              onClick={() => onCellClick?.(c)}
            >
              {sym}
            </div>
          </td>
        );
      });
      const right =
        mode === "sig"
          ? (() => {
              const a = signatureAggregate(rowCells);
              return a.req ? (
                <span style={{ color: readinessHex(a.pct), fontWeight: 800 }}>{a.pct}%</span>
              ) : (
                <span style={{ color: "#b6c2bf" }}>—</span>
              );
            })()
          : (() => {
              const a = aggregateCells(rowCells);
              return (
                <span style={{ color: readinessHex(a.pct), fontWeight: 800 }}>{a.pct}%</span>
              );
            })();
      return (
        <tr key={s.id}>
          <th title={s.name}>{s.name}</th>
          {tds}
          <td className="mrow">{right}</td>
        </tr>
      );
    });
    return (
      <table className="isf-cc__mtx">
        <thead>{head}</thead>
        <tbody>{body}</tbody>
      </table>
    );
  };

  const renderDocTable = (withSig) => {
    const cs = filterCells().sort((a, b) => {
      const o = { overdue: 0, pending: 1, fulfilled: 2 };
      return (o[a.status] ?? 9) - (o[b.status] ?? 9) || b.days - a.days;
    });
    return (
      <table className="isf-cc__tbl">
        <thead>
          <tr>
            <th>Site</th>
            <th>Milestone</th>
            <th>Document</th>
            <th>Owner</th>
            <th>Due</th>
            <th>Status</th>
            {withSig ? <th>Signature</th> : null}
            <th />
          </tr>
        </thead>
        <tbody>
          {cs.map((c) => (
            <tr key={`${c.code}-${c.siteId}-${c.studyPlaceholderId}`}>
              <td>{c.siteName}</td>
              <td>
                <b>{c.code}</b>
              </td>
              <td>{c.placeholderName || c.documentName || "—"}</td>
              <td>{c.owner}</td>
              <td style={{ color: c.status === "overdue" ? "var(--cc-red)" : "inherit", fontWeight: c.status === "overdue" ? 700 : 400 }}>
                {formatGbDate(c.due)}
                {c.status === "overdue" && c.days ? ` (${c.days}d)` : ""}
              </td>
              <td>
                <StatusBadge status={c.status} />
              </td>
              {withSig ? (
                <td>
                  <SigBadge cell={c} />
                </td>
              ) : null}
              <td>
                <button type="button" className="isf-cc__act" onClick={() => openIsf(c)}>
                  Open
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  const filterNote = (withSig) => {
    const cs = filterCells();
    let label = "All applicable documents";
    if (sel?.ms) {
      const siteLabel =
        sel.site && model?.sites
          ? model.sites.find((s) => s.id === sel.site)?.name
          : null;
      label = siteLabel ? `${sel.ms} · ${siteLabel}` : `${sel.ms}`;
    } else if (sel?.k) {
      label =
        {
          pending: "Pending",
          overdue: "Overdue",
          await: "Awaiting signature",
          atrisk: "At-risk",
          le7: "Due ≤ 7 days",
          d8_14: "Due 8–14 days",
          d15_30: "Due 15–30 days",
        }[sel.k] || sel.k;
    }
    const siteLabel = site?.value === "all" ? "All sites" : site?.label;
    return (
      <div className="isf-cc__filtnote">
        <button type="button" className="isf-cc__chip" onClick={() => sel && setSel(null)}>
          {siteLabel}
          {sel ? ` • ${label} ✕` : ""}
        </button>
        <span>
          {cs.length} document{cs.length !== 1 ? "s" : ""}
        </span>
      </div>
    );
  };

  const renderRoleBars = () => {
    const roles = [
      ...new Set(scopedCells.filter((c) => c.reqSig && c.signerRole).map((c) => c.signerRole)),
    ].filter(Boolean);
    if (!roles.length) return <p className="isf-cc__note">No signature-required roles configured.</p>;
    return (
      <>
        {roles.map((role) => {
          const rc = scopedCells.filter((c) => c.reqSig && c.signerRole === role);
          const a = signatureAggregate(rc);
          if (!a.req) return null;
          return (
            <div
              key={role}
              className="isf-cc__bar"
              role="button"
              tabIndex={0}
              onClick={() => setSel({ k: "await", role })}
            >
              <div className="lab">{role}</div>
              <div className="track">
                <span
                  className="isf-cc__seg isf-cc__seg--ful"
                  style={{ width: `${(100 * a.signed) / a.req}%` }}
                />
                <span
                  className="isf-cc__seg isf-cc__seg--await"
                  style={{ width: `${(100 * a.await) / a.req}%` }}
                />
              </div>
              <div className="val" style={{ color: readinessHex(a.pct) }}>
                {a.pct}%
              </div>
            </div>
          );
        })}
        <div className="isf-cc__legend">
          <span>
            <i style={{ background: "var(--cc-green)" }} /> Signed
          </span>
          <span>
            <i style={{ background: "var(--cc-amber)" }} /> Awaiting
          </span>
        </div>
      </>
    );
  };

  /** Drill into document table only — keep full matrix visible (do not change site filter). */
  const onMatrixCell = (c) => {
    setSel((prev) =>
      prev?.ms === c.code && prev?.site === c.siteId
        ? null
        : { ms: c.code, site: c.siteId },
    );
  };

  const renderCommandCenter = () => (
    <section className={`isf-cc__view${tab === "C" ? " on" : ""}`}>
      <div className="isf-cc__h">
        <h1>Study Command Center</h1>
        <span className="isf-cc__sub">
          The whole study at a glance — documents, signatures and what&apos;s coming
          {phaseLabel ? ` (${phaseLabel})` : ""}.
        </span>
      </div>
      <div className="isf-cc__kstrip">
        <KpiCard
          title="Doc readiness"
          value={`${docAgg.pct}%`}
          detail={`${docAgg.ful} of ${docAgg.req} fulfilled`}
          color={readinessCssVar(docAgg.pct)}
          barColor={readinessHex(docAgg.pct)}
          filterKey=""
          selected={!sel?.k}
          onClick={() => setSel(null)}
        />
        <KpiCard
          title="Signature ready"
          value={`${sigAgg.pct}%`}
          detail={`${sigAgg.signed} of ${sigAgg.req} signed`}
          color={readinessCssVar(sigAgg.pct)}
          barColor={readinessHex(sigAgg.pct)}
          filterKey="await"
          selected={sel?.k === "await"}
          onClick={toggleSel}
        />
        <KpiCard
          title="Overdue"
          value={String(docAgg.ovd)}
          detail="escalate now"
          color="var(--cc-red)"
          barColor="var(--cc-red)"
          filterKey="overdue"
          selected={sel?.k === "overdue"}
          onClick={toggleSel}
        />
        <KpiCard
          title="Awaiting sign"
          value={String(sigAgg.await)}
          detail={`incl. ${filedUnsigned} filed`}
          color="var(--cc-amber)"
          barColor="var(--cc-amber)"
          filterKey="await"
          selected={sel?.k === "await"}
          onClick={toggleSel}
        />
        <KpiCard
          title="Due ≤ 7d"
          value={String(docAgg.le7)}
          detail="this week"
          color="var(--cc-amber)"
          barColor="var(--cc-amber)"
          filterKey="le7"
          selected={sel?.k === "le7"}
          onClick={toggleSel}
        />
        <KpiCard
          title="At-risk"
          value={String(docAgg.ovd + filedUnsigned)}
          detail="overdue + unsigned"
          color="var(--cc-purple)"
          barColor="var(--cc-purple)"
          filterKey="atrisk"
          selected={sel?.k === "atrisk"}
          onClick={toggleSel}
        />
      </div>
      <div className="isf-cc__card">
        <h3>
          Cross-site readiness
          <span className="isf-cc__mut"> — sites × milestones</span>
          <span className="isf-cc__toggle">
            <button type="button" className={mmode === "docs" ? "on" : ""} onClick={() => setMmode("docs")}>
              Documents
            </button>
            <button type="button" className={mmode === "sig" ? "on" : ""} onClick={() => setMmode("sig")}>
              Signatures
            </button>
          </span>
        </h3>
        <div style={{ overflow: "auto" }}>{renderMatrix(mmode, onMatrixCell)}</div>
        <div className="isf-cc__legend">
          {mmode === "sig" ? (
            <>
              <span>
                <i style={{ background: CC_COLORS.signed }} /> Signed
              </span>
              <span>
                <i style={{ background: CC_COLORS.await }} /> Awaiting
              </span>
            </>
          ) : (
            <>
              <span>
                <i style={{ background: CC_COLORS.fulfilled }} /> Fulfilled
              </span>
              <span>
                <i style={{ background: CC_COLORS.pending }} /> Pending
              </span>
              <span>
                <i style={{ background: CC_COLORS.overdue }} /> Overdue
              </span>
            </>
          )}
        </div>
      </div>
      <div className="isf-cc__two">
        <div className="isf-cc__card">
          <h3>
            Signature readiness <span className="isf-cc__mut">— by signer role</span>
          </h3>
          <div style={{ display: "flex", gap: 18, alignItems: "center" }}>
            <div className="isf-cc__gauge">
              <Gauge percent={sigAgg.pct} label="signed" size={132} />
            </div>
            <div style={{ flex: 1 }}>{renderRoleBars()}</div>
          </div>
          <p className="isf-cc__note">
            {filedUnsigned} document{filedUnsigned !== 1 ? "s" : ""} filed but still awaiting signature.
          </p>
        </div>
        <div className="isf-cc__card">
          <h3>
            Due forecast <span className="isf-cc__mut">— pending by window</span>
          </h3>
          <div className="isf-cc__fc">
            {[
              ["≤ 7 days", "le7", "var(--cc-amber)", docAgg.le7],
              ["8–14 days", "d8_14", "#C9A227", docAgg.d8_14],
              ["15–30 days", "d15_30", "#9fb0ac", docAgg.d15_30],
              ["> 30 days", "gt30", "#c4cecc", docAgg.gt30],
            ].map(([label, key, col, n]) => (
              <div
                key={key}
                className="isf-cc__fcb"
                role="button"
                tabIndex={0}
                onClick={() => setSel({ k: key })}
              >
                <div className="fct">{label}</div>
                <div className="fcv" style={{ color: col }}>
                  {n || 0}
                </div>
                <div className="fcm">due</div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="isf-cc__two">
        <div className="isf-cc__card">
          <h3>
            At-risk feed <span className="isf-cc__mut">— overdue · awaiting sign</span>
          </h3>
          <div className="isf-cc__feed">
            {scopedCells
              .filter(
                (c) =>
                  c.status === "overdue" ||
                  (c.status === "fulfilled" && c.sig === "await") ||
                  (c.status === "pending" && c.bucket === "le7"),
              )
              .slice(0, 9)
              .map((c) => (
                <div
                  key={`${c.code}-${c.siteId}`}
                  className="isf-cc__fi"
                  role="button"
                  tabIndex={0}
                  onClick={() => onMatrixCell(c)}
                >
                  <div
                    className="isf-cc__fic"
                    style={{
                      background:
                        c.status === "overdue" ? CC_COLORS.overdue : CC_COLORS.await,
                    }}
                  >
                    {c.status === "overdue" ? "!" : "✎"}
                  </div>
                  <div>
                    <div className="isf-cc__ft">
                      {c.code} · {c.siteName}
                    </div>
                    <div className="isf-cc__fm">{c.placeholderName}</div>
                  </div>
                  <div
                    className="isf-cc__fr"
                    style={{
                      background:
                        c.status === "overdue" ? "var(--cc-red-bg)" : "var(--cc-amber-bg)",
                      color: c.status === "overdue" ? "var(--cc-red)" : "var(--cc-amber)",
                    }}
                  >
                    {c.status === "overdue" ? `Overdue ${c.days}d` : "Awaiting sign"}
                  </div>
                </div>
              ))}
          </div>
        </div>
        <div className="isf-cc__card">
          <h3>Document detail</h3>
          {filterNote(true)}
          <div style={{ maxHeight: 360, overflow: "auto" }}>{renderDocTable(true)}</div>
        </div>
      </div>
    </section>
  );

  const renderInspection = () => (
    <section className={`isf-cc__view${tab === "1" ? " on" : ""}`}>
      <div className="isf-cc__h">
        <h1>Inspection Readiness</h1>
        <span className="isf-cc__sub">Is the study ready right now — and where is it weakest?</span>
      </div>
      <div className="isf-cc__kstrip">
        <KpiCard title="Readiness" value={`${docAgg.pct}%`} color={readinessCssVar(docAgg.pct)} barColor={readinessHex(docAgg.pct)} onClick={() => setSel(null)} />
        <KpiCard title="Pending" value={String(docAgg.pen)} color="var(--cc-blue)" barColor="var(--cc-blue)" filterKey="pending" selected={sel?.k === "pending"} onClick={toggleSel} />
        <KpiCard title="Overdue" value={String(docAgg.ovd)} color="var(--cc-red)" barColor="var(--cc-red)" filterKey="overdue" selected={sel?.k === "overdue"} onClick={toggleSel} />
        <KpiCard title="Due ≤ 7d" value={String(docAgg.le7)} color="var(--cc-amber)" barColor="var(--cc-amber)" filterKey="le7" selected={sel?.k === "le7"} onClick={toggleSel} />
        <KpiCard title="Due 8–14d" value={String(docAgg.d8_14)} color="#C9A227" barColor="#C9A227" filterKey="d8_14" selected={sel?.k === "d8_14"} onClick={toggleSel} />
        <KpiCard title="Due 15–30d" value={String(docAgg.d15_30)} color="var(--cc-mut)" barColor="#cfd8d6" filterKey="d15_30" selected={sel?.k === "d15_30"} onClick={toggleSel} />
      </div>
      <div className="isf-cc__grid2">
        <div className="isf-cc__card">
          <h3>Overall readiness</h3>
          <div className="isf-cc__gauge">
            <Gauge percent={docAgg.pct} label="ready" size={190} />
            <p className="isf-cc__gauge-cap">
              {docAgg.ful} of {docAgg.req} required fulfilled
            </p>
          </div>
        </div>
        <div className="isf-cc__card">
          <h3>
            By milestone <span className="isf-cc__mut">— fulfilled / pending / overdue</span>
          </h3>
          {(model?.milestones || []).map((m) => {
            const c = scopedCells.filter((x) => x.code === m.code);
            const x = aggregateCells(c);
            if (!x.req) return null;
            const seg = (n, kind) =>
              n ? (
                <span
                  className={`isf-cc__seg isf-cc__seg--${kind}`}
                  style={{ width: `${(100 * n) / x.req}%` }}
                />
              ) : null;
            return (
              <div
                key={m.code}
                className="isf-cc__msbar"
                role="button"
                tabIndex={0}
                onClick={() => setSel({ ms: m.code })}
              >
                <div className="code" title={m.name}>
                  {m.code}
                </div>
                <div className="track">
                  {seg(x.ful, "ful")}
                  {seg(x.pen, "pen")}
                  {seg(x.ovd, "ovd")}
                </div>
                <div className="pct" style={{ color: readinessHex(x.pct) }}>
                  {x.pct}%
                </div>
              </div>
            );
          })}
          <div className="isf-cc__legend" style={{ marginTop: 12 }}>
            <span>
              <i style={{ background: "var(--cc-green)" }} /> Fulfilled
            </span>
            <span>
              <i style={{ background: "var(--cc-blue)" }} /> Pending
            </span>
            <span>
              <i style={{ background: "var(--cc-red)" }} /> Overdue
            </span>
          </div>
        </div>
      </div>
      <div className="isf-cc__card">
        <h3>Document detail</h3>
        {filterNote(false)}
        {renderDocTable(false)}
      </div>
    </section>
  );

  const renderSiteComparison = () => {
    const allCells = model ? scopeCells(model.cells, "all") : [];
    const all = aggregateCells(allCells);
    const rows = (model?.sites || [])
      .filter((s) => s.id !== "__study__")
      .map((s) => ({
        s,
        x: aggregateCells(allCells.filter((c) => c.siteId === s.id)),
      }))
      .sort((a, b) => b.x.pct - a.x.pct);
    return (
      <section className={`isf-cc__view${tab === "4" ? " on" : ""}`}>
        <div className="isf-cc__h">
          <h1>Site Comparison</h1>
          <span className="isf-cc__sub">How sites rank — and which need attention first.</span>
        </div>
        <div className="isf-cc__kstrip isf-cc__kstrip--4">
          <KpiCard title="Study average" value={`${all.pct}%`} color={readinessCssVar(all.pct)} barColor={readinessHex(all.pct)} onClick={() => {}} />
          <KpiCard title="Best site" value={rows[0] ? `${rows[0].x.pct}%` : "—"} color="var(--cc-green)" barColor="var(--cc-green)" onClick={() => {}} />
          <KpiCard title="Lowest site" value={rows.length ? `${rows[rows.length - 1].x.pct}%` : "—"} color="var(--cc-red)" barColor="var(--cc-red)" onClick={() => {}} />
          <KpiCard title="Sites tracked" value={String(rows.length)} onClick={() => {}} />
        </div>
        <div className="isf-cc__card">
          <h3>
            Readiness by site <span className="isf-cc__mut">— ranked</span>
          </h3>
          {rows.map(({ s, x }) => (
            <div
              key={s.id}
              className="isf-cc__rankrow"
              role="button"
              tabIndex={0}
              onClick={() => {
                setSite({ label: s.name, value: s.id });
                setTab("1");
              }}
            >
              <div className="rn">{s.name}</div>
              <div className="rt">
                <div className="rf" style={{ width: `${x.pct}%`, background: readinessHex(x.pct) }} />
              </div>
              <div className="rp" style={{ color: readinessHex(x.pct) }}>
                {x.pct}%
              </div>
            </div>
          ))}
        </div>
        <div className="isf-cc__card">
          <h3>Cross-site matrix</h3>
          <div style={{ overflow: "auto" }}>{renderMatrix("docs", onMatrixCell)}</div>
        </div>
      </section>
    );
  };

  const snapshotLabel = asOf.toLocaleString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

  return (
    <div className="isf-cc isf-dashboard">
      <PageBreadcrumb
        items={[
          { label: "Home", path: "/notification-center" },
          { label: "eISF", path: "/isf" },
          { label: "Dashboard" },
        ]}
        actions={[
          { icon: "fas fa-sync-alt", label: "Refresh", onClick: refreshDashboard },
        ]}
        toolbarExtras={
          model ? (
            <span className="isf-cc__asof-inline">
              Snapshot as of <b>{snapshotLabel}</b>
            </span>
          ) : null
        }
      />

      {loadError ? <div className="isf-cc__alert">{loadError}</div> : null}

      <div className="isf-cc__wrap">
        <aside className="isf-cc__rail">
          <div className="isf-cc__rl">Filters</div>
          <div className="isf-cc__field">
            <label>Study</label>
            <Dropdown
              options={studyOptions}
              selected={
                studiesLoading
                  ? { label: "Loading…", value: "__loading" }
                  : study || { label: "Select study", value: "" }
              }
              onSelect={setStudy}
              disabled={studiesLoading || !studyOptions.length}
            />
          </div>
          <div className="isf-cc__field">
            <label>Category</label>
            <Dropdown options={CATEGORY_OPTIONS} selected={category} onSelect={setCategory} />
          </div>
          <div className="isf-cc__field">
            <label>Site</label>
            <Dropdown
              options={siteOptions}
              selected={site}
              onSelect={(opt) => {
                setSite(opt);
                setSel(null);
              }}
              disabled={!model}
            />
          </div>
          {studyGate ? (
            <div className="isf-cc__field isf-cc__field--gate">
              <label>Study phase</label>
              <IsfPhaseFilter
                gates={studyGateList}
                value={selectedGateCodes}
                onChange={setSelectedGateCodes}
                disabled={gateLoading}
              />
            </div>
          ) : null}
          {studyGate ? (
            <div className="isf-cc__gate-progress-wrap">
              <StudyGateProgressStrip studyGateProgress={studyGateProgress} />
            </div>
          ) : null}
          <div className="isf-cc__rl">Views</div>
          <div className="isf-cc__tabs">
            {TABS.map((t) => (
              <button
                key={t.id}
                type="button"
                className={`isf-cc__tab${t.hero ? " hero" : ""}${tab === t.id ? " on" : ""}`}
                onClick={() => {
                  setTab(t.id);
                  setSel(null);
                }}
              >
                <span className="isf-cc__tab-ic" />
                {t.label}
              </button>
            ))}
          </div>
          {model ? (
            <div className="isf-cc__summary">
              <div className="isf-cc__sr">
                <span>Doc readiness</span>
                <span style={{ color: readinessHex(docAgg.pct) }}>{docAgg.pct}%</span>
              </div>
              <div className="isf-cc__sr">
                <span>Signature ready</span>
                <span style={{ color: readinessHex(sigAgg.pct) }}>{sigAgg.pct}%</span>
              </div>
              <div className="isf-cc__sr">
                <span>Overdue</span>
                <span style={{ color: "var(--cc-red)" }}>{docAgg.ovd}</span>
              </div>
              <div className="isf-cc__sr">
                <span>Awaiting sign</span>
                <span style={{ color: "var(--cc-amber)" }}>{sigAgg.await}</span>
              </div>
              <div className="isf-cc__sr">
                <span>Applicable</span>
                <span>{docAgg.req}</span>
              </div>
            </div>
          ) : null}
          <p style={{ fontSize: "10.5px", color: "var(--cc-mut)", marginTop: 12 }}>
            Readiness = Fulfilled ÷ Applicable. Signatures = Signed ÷ Signature-required.
          </p>
        </aside>

        <main className="isf-cc__main">
          {loading ? (
            <div className="isf-cc__loading">
              <div className="spinner-border spinner-border-sm" role="status" />
              <span>Loading dashboard…</span>
            </div>
          ) : null}

          {!study?.value ? (
            <div className="isf-cc__empty">
              <h2>Select a study</h2>
              <p>Choose a study in the left rail to load the dashboard.</p>
            </div>
          ) : (
            <>
              {renderCommandCenter()}
              <section className={`isf-cc__view${tab === "S" ? " on" : ""}`}>
                <div className="isf-cc__h">
                  <h1>Signatures</h1>
                  <span className="isf-cc__sub">Signature-required documents — status by site and role.</span>
                </div>
                <div className="isf-cc__kstrip isf-cc__kstrip--4">
                  <KpiCard title="Signature ready" value={`${sigAgg.pct}%`} color={readinessCssVar(sigAgg.pct)} barColor={readinessHex(sigAgg.pct)} onClick={() => {}} />
                  <KpiCard title="Signed" value={String(sigAgg.signed)} color="var(--cc-green)" barColor="var(--cc-green)" onClick={() => {}} />
                  <KpiCard title="Awaiting" value={String(sigAgg.await)} color="var(--cc-amber)" barColor="var(--cc-amber)" filterKey="await" selected={sel?.k === "await"} onClick={toggleSel} />
                  <KpiCard title="Filed, unsigned" value={String(filedUnsigned)} color="var(--cc-purple)" barColor="var(--cc-purple)" onClick={() => {}} />
                </div>
                <div className="isf-cc__two">
                  <div className="isf-cc__card">
                    <h3>By signer role</h3>
                    {renderRoleBars()}
                  </div>
                  <div className="isf-cc__card">
                    <h3>Signature readiness by site</h3>
                    {(model?.sites || [])
                      .filter((s) => s.id !== "__study__")
                      .map((s) => {
                        const a = signatureAggregate(
                          scopedCells.filter((c) => c.siteId === s.id),
                        );
                        if (!a.req) return null;
                        return (
                          <div
                            key={s.id}
                            className="isf-cc__rankrow"
                            role="button"
                            tabIndex={0}
                            onClick={() => setSite({ label: s.name, value: s.id })}
                          >
                            <div className="rn">{s.name}</div>
                            <div className="rt">
                              <div className="rf" style={{ width: `${a.pct}%`, background: readinessHex(a.pct) }} />
                            </div>
                            <div className="rp" style={{ color: readinessHex(a.pct) }}>
                              {a.pct}%
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </div>
                <div className="isf-cc__card">
                  <h3>Signature-required documents</h3>
                  {filterNote(true)}
                  {renderDocTable(true)}
                </div>
              </section>
              <section className={`isf-cc__view${tab === "2" ? " on" : ""}`}>
                <div className="isf-cc__h">
                  <h1>Missing Required</h1>
                  <span className="isf-cc__sub">Required documents not yet fulfilled.</span>
                </div>
                <div className="isf-cc__kstrip isf-cc__kstrip--4">
                  <KpiCard title="Outstanding" value={String(docAgg.pen + docAgg.ovd)} onClick={() => {}} />
                  <KpiCard title="Overdue" value={String(docAgg.ovd)} color="var(--cc-red)" barColor="var(--cc-red)" filterKey="overdue" selected={sel?.k === "overdue"} onClick={toggleSel} />
                  <KpiCard title="Pending" value={String(docAgg.pen)} color="var(--cc-blue)" barColor="var(--cc-blue)" filterKey="pending" selected={sel?.k === "pending"} onClick={toggleSel} />
                  <KpiCard title="% outstanding" value={`${docAgg.req ? Math.round((1000 * (docAgg.pen + docAgg.ovd)) / docAgg.req) / 10 : 0}%`} onClick={() => {}} />
                </div>
                <div className="isf-cc__card">
                  <h3>Outstanding documents</h3>
                  {filterNote(false)}
                  {renderDocTable(false)}
                </div>
              </section>
              <section className={`isf-cc__view${tab === "3" ? " on" : ""}`}>
                <div className="isf-cc__h">
                  <h1>Overdue &amp; At-Risk</h1>
                  <span className="isf-cc__sub">What&apos;s overdue and due soon.</span>
                </div>
                <div className="isf-cc__kstrip isf-cc__kstrip--4">
                  <KpiCard title="Overdue" value={String(docAgg.ovd)} color="var(--cc-red)" barColor="var(--cc-red)" filterKey="overdue" selected={sel?.k === "overdue"} onClick={toggleSel} />
                  <KpiCard title="Due ≤ 7d" value={String(docAgg.le7)} color="var(--cc-amber)" barColor="var(--cc-amber)" filterKey="le7" selected={sel?.k === "le7"} onClick={toggleSel} />
                  <KpiCard title="Due 8–14d" value={String(docAgg.d8_14)} color="#C9A227" barColor="#C9A227" filterKey="d8_14" selected={sel?.k === "d8_14"} onClick={toggleSel} />
                  <KpiCard title="Due 15–30d" value={String(docAgg.d15_30)} onClick={() => {}} />
                </div>
                <div className="isf-cc__card">
                  <h3>At-risk documents</h3>
                  {filterNote(false)}
                  {renderDocTable(false)}
                </div>
              </section>
              {renderInspection()}
              {renderSiteComparison()}
            </>
          )}
        </main>
      </div>
    </div>
  );
}
